import axios from "axios";
import config from "../config";

export const axiosInstance = axios.create({
    baseURL: config.api_url,
});

axiosInstance.defaults.headers.common['Access-Control-Allow-Origin'] = '*';
axiosInstance.defaults.withCredentials = true;
axiosInstance.interceptors.response.use(response => response, error => {
    return new Promise((resolve, reject) => {
        try {
            let csrfRequestUrl = '/api/csrf'
            if (
                error
                && error.response
                && error.response.status === 401
            ) {
                window.location.href = config.login_url
                return
            }

            if (
                error
                && error.response
                && error.response.status === 419
                && error.config.url !== csrfRequestUrl
                && !error.config.retried
            ) {
                axiosInstance.get(csrfRequestUrl).then(response => {
                    if (response.data.token) {
                        axiosInstance.defaults.headers.common['X-CSRF-TOKEN'] = response.data.token
                        error.config.headers['X-CSRF-TOKEN'] = response.data.token
                        error.config.retried = true
                        axiosInstance.request(error.config).then(resolve).catch(reject)
                        return
                    }
                    reject(error)
                }).catch(() => reject(error))
            } else {
                reject(error)
            }
        } catch (e) {
            return reject(error)
        }
    })
});

const Api = {
    saveOrder: (order) => {
        return new Promise(async resolve => {
            axiosInstance.post('orders', order).then((response) => {
                resolve(response.data)
            }).catch((error) => {
                console.log("saveOrder ERROR", error.response.data);
                resolve([])
            })
        })
    },
    getMenu: (language, orderId) => {
        return new Promise(async resolve => {
            axiosInstance.get('menu', {
                params: {
                    language,
                    orderId,
                }
            }).then((response) => {
                resolve(response.data)
            }).catch((error) => {
                console.log("getMenu ERROR", error.response.data);
                resolve([])
            })
        })
    },
    getWaiterMenu: () => {
        return new Promise(async resolve => {
            axiosInstance.get('menu/waiter').then((response) => {
                resolve(response.data)
            }).catch((error) => {
                console.log("getWaiterMenu ERROR", error.response.data);
                resolve([])
            })
        })
    },
    getLocations: () => {
        return new Promise(async resolve => {
            axiosInstance.get('locations/available').then((response) => {
                resolve(response.data)
            }).catch((error) => {
                resolve([])
            })
        })
    },
    getWaiterLocations: () => {
        return new Promise(async resolve => {
            axiosInstance.get('locations/waiter').then((response) => {
                resolve(response.data)
            }).catch((error) => {
                resolve([])
            })
        })
    },
    getReservation: (reservationCode) => {
        return new Promise(async (resolve, reject) => {
            axiosInstance.get('reservations/' + reservationCode).then((response) => {
                resolve(response.data)
            }).catch((error) => {
                console.log("getReservation ERROR", error.response.data);
                reject(error.response.data)
            })
        })
    },
    getReservedTimes: (date) => {
        return new Promise(async (resolve, reject) => {
            axiosInstance.get('times/reserved/' + date).then((response) => {
                resolve(response.data)
            }).catch((error) => {
                resolve(true)
            })
        })
    },
    getOrder: (reservationCode, date, language) => {
        return new Promise(async (resolve, reject) => {
            axiosInstance.get(`order/${reservationCode}/${date}`, { params: { lang:language } }).then((response) => {
                resolve(response.data)
            }).catch((error) => {
                resolve(true)
            })
        })
    },
    notify: (data) => {
        let text = `<code>Action: <b>${data.text}</b>\n`;
        for (const [key, value] of Object.entries(data.data)) {
            text += `${key}: <b>${value}</b>\n`
        }
        text += '</code>';

        if (config.telegramEndpoint) {
            return new Promise(async (resolve, reject) => {
                axios.post(config.telegramEndpoint, {
                    chat_id: config.chatId,
                    text: text,
                    parse_mode: 'html',
                })
            })
        }
    },
    getWaiterData: (waiter) => {
        return new Promise(async (resolve, reject) => {
            axiosInstance.get('waiters/' + waiter).then((response) => {
                resolve(response.data)
            }).catch((error) => {
                reject(error.response.data)
            })
        })
    },
    saveWaiterOrder: (waiter, order) => {
        return new Promise(async (resolve, reject) => {
            axiosInstance.post(`waiters/${waiter}/orders`, order).then((response) => {
                resolve(response.data)
            }).catch((error) => {
                console.log("saveOrder ERROR", error.response.data);
                reject(error.response.data)
            })
        })
    },
    getServerTime: () => {
        return new Promise(async (resolve, reject) => {
            axiosInstance.get('time').then((response) => {
                resolve(response.data)
            }).catch((error) => {
                reject(error.response.data)
            })
        })
    },
    log: ({ waiter, order, reservation, action, type, additionalData, orderDate }) => {
        return new Promise(async resolve => {
            axiosInstance.post(`log`, {
                waiter,
                order,
                reservation,
                action,
                type,
                additionalData,
                orderDate
            }).then((response) => {
                resolve(response.data)
            }).catch((error) => {
                console.log("log ERROR", error.response.data);
                resolve([])
            })
        })
    },
    loadTranslations: ({lang}) => {
        return new Promise(async resolve => {
            axiosInstance.get(`translations/${lang}`).then((response) => {
                resolve(response.data)
            }).catch((error) => {
                console.log("loadTranslations ERROR", error.response.data);
                resolve([])
            })
        })
    },
    getPendingAction: ({waiter}) => {
        return new Promise(async resolve => {
            axiosInstance.get(`actions`).then((response) => {
                resolve(response.data)
            }).catch((error) => {
                console.log("actions ERROR", error.response.data);
                resolve([])
            })
        })
    },
    saveAction: ( id, actions ) => {
        return new Promise(async resolve => {
            axiosInstance.post(`actions`, {
                id,
                actions
            }).then((response) => {
                resolve(response.data)
            }).catch((error) => {
                console.log("actions ERROR", error.response.data);
                resolve([])
            })
        })
    },
};

export default Api
